/**
 * Which organization am I working in?
 *
 * Shown when the caller holds compliance access in several organizations and
 * the session has not been told which one to act in — the backend's
 * `ORGANIZATION_SELECTION_REQUIRED`. It refuses rather than guessing on purpose:
 * this app approves batches and files RFS and LCFS reports, so landing someone
 * in an arbitrary customer's books would put a regulatory sign-off behind a coin
 * toss.
 *
 * Deliberately not the `AccountInactive` screen. That one is terminal and offers
 * a sign-out; this is a question with answers on it.
 */

import { Button, Card, Center, Group, Radio, Stack, Text, Title } from "@mantine/core";
import { IconBuilding } from "@tabler/icons-react";
import { useState } from "react";
import type { OrganizationGrant } from "../api/types";
import { useAuth } from "./useAuth";

export function OrganizationPicker({ candidates }: { candidates: OrganizationGrant[] }) {
  const { selectOrganization, signOut } = useAuth();
  // Nothing pre-selected: the whole point is that the person says which, and a
  // default is the silent pick this screen exists to avoid.
  const [chosen, setChosen] = useState<string | null>(null);
  const [busyWarning, setBusyWarning] = useState(false);

  // A refusal with no candidates is a real state, not a rendering bug: the
  // grants can be revoked between the refusal and this render, and a picker
  // with nothing in it has to say so rather than show an empty box.
  if (candidates.length === 0) {
    return (
      <Center h="100vh" p="md">
        <Card withBorder radius="md" p="xl" maw={460}>
          <Stack gap="sm">
            <Title order={4}>No organizations available</Title>
            <Text size="sm" c="dimmed">
              Your account needs compliance access to an organization before you can work here. Ask
              a compliance admin to grant it.
            </Text>
            <Button variant="light" onClick={() => void signOut()} mt="xs">
              Sign out
            </Button>
          </Stack>
        </Card>
      </Center>
    );
  }

  const confirm = () => {
    if (!chosen) return;
    // False means a mutation is still in flight and switching now would discard
    // its response — say so instead of silently doing nothing.
    if (!selectOrganization(chosen)) {
      setBusyWarning(true);
      return;
    }
    setBusyWarning(false);
  };

  return (
    <Center h="100vh" p="md">
      <Card withBorder radius="md" p="xl" maw={460} w="100%">
        <Stack gap="lg">
          <Stack gap={6}>
            <Group gap="xs">
              <IconBuilding size={20} />
              <Title order={4}>Choose an organization</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Your account has compliance access to more than one. Everything you do — batches,
              uploads, corrections, reports — applies to the one you pick.
            </Text>
          </Stack>

          <Radio.Group value={chosen ?? ""} onChange={setChosen} aria-label="Organization">
            <Stack gap="xs">
              {candidates.map((candidate) => (
                <Radio.Card
                  key={candidate.organization_id}
                  value={candidate.organization_id}
                  p="sm"
                  radius="sm"
                >
                  <Group wrap="nowrap" align="center" gap="sm">
                    <Radio.Indicator />
                    <Stack gap={0}>
                      <Text size="sm" fw={600}>
                        {candidate.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {candidate.role}
                      </Text>
                    </Stack>
                  </Group>
                </Radio.Card>
              ))}
            </Stack>
          </Radio.Group>

          {busyWarning ? (
            <Text size="xs" c="orange">
              A change is still saving. Wait for it to finish before switching, so its result isn't
              lost.
            </Text>
          ) : null}

          <Group justify="space-between">
            <Button variant="subtle" size="sm" onClick={() => void signOut()}>
              Sign out
            </Button>
            <Button onClick={confirm} disabled={!chosen}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Card>
    </Center>
  );
}
